import {type Context, datadogRum} from '@datadog/browser-rum';

import useTracking from '@/hooks/useTracking';
import {NAVLINK_TRACKING} from '../../appBarTracking';
import useNavbarVersion from '../../hooks/useNavbarVersion';
import {type DraftCartTrackingFields} from './getDraftCartTrackingFields';

type DraftCartElement = {elementName: string; datadogAction: string};

type FireParams = {element: DraftCartElement; payload: Context};

type CatererClickParams = {catererId: string; fields: DraftCartTrackingFields};

const useDraftCartTracking = () => {
  const {trackClick} = useTracking();
  const version = useNavbarVersion('draftCart');

  const fire = ({element, payload}: FireParams) => {
    const properties = {...payload, version};
    trackClick(element.elementName, {misc_json: JSON.stringify(properties)});
    datadogRum.addAction(element.datadogAction, properties);
  };

  const {card, continueOrder, deleteClosed, deleteConfirm, seeAll} = NAVLINK_TRACKING.cart;

  return {
    trackCardClick: ({catererId, fields}: CatererClickParams) =>
      fire({element: card, payload: {caterer_id: catererId, ...fields}}),
    trackContinueClick: ({catererId, fields}: CatererClickParams) =>
      fire({element: continueOrder, payload: {caterer_id: catererId, ...fields}}),
    // `order_id` predates `cart_id` and holds the same UUID;
    // it stays because we don't want to break existing tracking.
    trackDeleteClick: (fields: DraftCartTrackingFields) =>
      fire({element: NAVLINK_TRACKING.cart.delete, payload: {order_id: fields.cart_id, ...fields}}),
    trackDeleteConfirm: (fields: DraftCartTrackingFields) =>
      fire({element: deleteConfirm, payload: {order_id: fields.cart_id, ...fields}}),
    trackDeleteClosed: (fields: DraftCartTrackingFields) =>
      fire({element: deleteClosed, payload: {order_id: fields.cart_id, ...fields}}),
    trackSeeAllClick: () => fire({element: seeAll, payload: {}}),
  };
};

export default useDraftCartTracking;
